import { ChatCmdType, MessageBody } from "@/utils/agora-rtm-client";
import EventEmitter from "events";
import {
  BASE_URL,
  MAX_USERS_STREAMS,
  QUERY_STRING_SEPARATOR,
  INJECT_STREAM_ID,
  SECOND_INJECT_STREAM_ID,
  SCREEN_SHARE_STREAM_ID,
} from "@/utils/config";
import { abbreviateNumber } from "@/utils/helper";
import AgoraRTC from "agora-rtc-sdk-ng";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  AgoraStream,
  AgoraMediaStream,
  ChatMessage,
  AgoraClient,
} from "@/utils/types";
import { Subject } from "rxjs";
import { Map, Set, List } from "immutable";
import { get, set } from "lodash";
import AgoraRTMClient, { ChannelParams } from "@/utils/agora-rtm-client";
import { AgoraWebClient } from "@/utils/agora-web-client";
import { APP_ID } from "@/utils/config";
import Axios from "axios";
import { ApiUrls } from "@/utils/api/constant/api.constant";

export interface AgoraUser {
  uid: any;
  role: number;
  video: number;
  audio: number;
}

export interface Me extends AgoraUser {
  channelName: string;
  publishLocalStream?: boolean;
  mainLiveStreamID?: number; // this is only used when we are are live show screen to handle default main stream.
  info?: any;
}

type RtcState = {
  published: boolean;
  joined: boolean;
  users: Set<number>;
  localStream: AgoraMediaStream | null;
  remoteStreams: Map<string, AgoraMediaStream>;
  injectedStream?: AgoraMediaStream | null;
  injected: boolean; // rtmp injection by host
  isAutoPlayFailed: boolean;
};

export type MediaDeviceState = {
  microphoneId: string;
  speakerId: string;
  cameraId: string;
  speakerVolume: number;
  camera: number;
  microphone: number;
  speaker: number;
};

export type RtmState = {
  joined: boolean;
};

export type EventRoomState = {
  appID: string;
  rtcToken: string;
  me: Me;
  usersAsAudience: Array<Object>;
  rtc: RtcState;
  rtm: RtmState;
  mediaDevice: MediaDeviceState;
  messages: List<ChatMessage>;
  privateMessages: List<ChatMessage>;
  viewerCount: number;
  isLive: boolean;
  networkQuality: number;
  currentMicrophone: any;
  currentCamera: any;
  currentSpeaker: any;
  gridView: boolean;
  hasStarted: boolean;
  is_residence_silent: boolean;
  is_noise_cancellation_on: boolean;
  is_technical_visit: boolean;
  pinId: any;
  isMessageSent: boolean;
  waitingList: any;
  hasJoinedVisit: boolean;
  showAdminVideoPlayer: boolean;
};

export class EventRoomStore {
  private history: any;
  setHistory(history: any) {
    this.history = history;
  }
  private subject: Subject<EventRoomState> | null;
  public _state: EventRoomState;

  get state() {
    return this._state;
  }

  set state(newState) {
    this._state = newState;
  }
  public readonly defaultState: EventRoomState = Object.freeze({
    appID: APP_ID,
    rtcToken: "",
    me: {
      uid: 0,
      role: 1,
      video: 1,
      audio: 1,
      channelName: "",
      publishLocalStream: false,
      mainLiveStreamID: 0,
      info: {
        uid: 0,
        name: "",
        role: "",
        profile_pic: "",
        guide: {
          id: 0,
          name: "",
          profile_pic: "",
          profile_url: "",
          userRole: "",
        },
      },
      host: {
        id: 0,
        name: "",
        userRole: "",
      },
      residence: {
        id: 0,
        name: "",
        userRole: "",
      },
      invitees: {
        id: 0,
        name: "",
        userRole: "",
      },
      admin: {
        id: 0,
        name: "",
        userRole: "",
      },
    },
    usersAsAudience: Array<Object>(),
    hasJoinedVisit: false,
    showAdminVideoPlayer: true,
    isLive: true,
    networkQuality: 0,
    is_residence_silent: true,
    is_noise_cancellation_on: true,
    is_technical_visit: false,
    rtm: {
      joined: false,
    },
    rtc: {
      published: false,
      joined: false,
      users: Set<number>(),
      localStream: null,
      remoteStreams: Map<string, AgoraMediaStream>(),
      injectedStream: null,
      injected: false,
      isAutoPlayFailed: true,
    },
    mediaDevice: {
      microphoneId: "",
      speakerId: "",
      cameraId: "",
      speakerVolume: 100,
      camera: 0,
      speaker: 0,
      microphone: 0,
    },
    currentMicrophone: "",
    currentCamera: "",
    currentSpeaker: "",
    messages: List<ChatMessage>(),
    privateMessages: List<ChatMessage>(),
    isMessageSent: false,
    viewerCount: 0,
    gridView: false,
    hasStarted: Boolean(sessionStorage.getItem("visitStarted")) || false,
    pinId: 0,
    waitingList: [],
  });

  public rtmClient: AgoraRTMClient;
  public rtcClient: AgoraWebClient;
  public rtcFanWallClients: AgoraClient[];
  public _emitter: EventEmitter;

  constructor() {
    this.subject = null;
    this._state = {
      ...this.defaultState,
    };
    this.rtmClient = new AgoraRTMClient(this);
    this.rtcClient = new AgoraWebClient({ eventRoomStore: this });
    this.rtcFanWallClients = [];
    this._emitter = new EventEmitter();
  }

  private getAuthToken() {
    return Cookies.get("accessToken") || Cookies.get("token") || "";
  }

  private getAuthHeaders() {
    const token = this.getAuthToken();
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }

  private formatDisplayDate(isoDate: string) {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return isoDate;
    }

    return date.toLocaleString("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  }

  private combineDateTime(dateValue: any, timeValue: any) {
    if (!dateValue || !timeValue) {
      return "";
    }

    const rawTime = String(timeValue);
    const normalizedTime = rawTime.length === 5 ? `${rawTime}:00` : rawTime;
    const candidate = `${dateValue}T${normalizedTime}`;
    const parsed = new Date(candidate);
    return Number.isNaN(parsed.getTime()) ? candidate : parsed.toISOString();
  }

  private formatUserName(user: any) {
    if (!user) {
      return "";
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    return fullName || user.username || user.email || "";
  }

  private resolveLocalizedValue(value: any) {
    if (!value) {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "object") {
      return value.fr || value.en || Object.values(value).find((entry) => Boolean(entry)) || "";
    }

    return String(value);
  }

  public resolveUserRole(currentUser: any) {
    if (!currentUser) {
      return "";
    }

    if (this.isGuideUser(currentUser)) {
      return "guide";
    }
    if (this.isAdminUser(currentUser)) {
      return "admin";
    }
    if (this.isHostUser(currentUser)) {
      return "host";
    }
    if (this.isResidenceUser(currentUser)) {
      return "residence";
    }
    return "";
  }

  private extractVisitTokenFromLink(link: string) {
    if (!link) {
      return "";
    }

    try {
      const parsed = new URL(link, window.location.origin);
      const parts = parsed.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      const noQuery = String(link).split("?")[0];
      const parts = noQuery.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    }
  }

  private resolveRoleFromCurrentVisitLink(visit: any) {
    if (!Array.isArray(visit?.visit_links) || typeof window === "undefined") {
      return "";
    }

    const currentPath = window.location.pathname;
    const currentParts = currentPath.split("/").filter(Boolean);
    const currentToken = currentParts[currentParts.length - 1] || "";
    if (!currentToken) {
      return "";
    }

    const match = visit.visit_links.find((entry: any) => {
      const token = this.extractVisitTokenFromLink(entry?.link || "");
      return token === currentToken;
    });

    return String(match?.role || "").toLowerCase();
  }

  // Public: the event page needs the SAME derivation for the logged-in uid,
  // or the numeric id won't match the participants list built here.
  toAgoraNumericId(value: any) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.trunc(value);
    }

    const raw = String(value || "").trim();
    if (!raw) {
      return 0;
    }

    if (/^\d+$/.test(raw)) {
      return parseInt(raw, 10);
    }

    let hash = 0;
    for (let index = 0; index < raw.length; index += 1) {
      hash = (hash * 31 + raw.charCodeAt(index)) >>> 0;
    }

    // Keep IDs in a stable positive range for Agora numeric UID usage.
    return (hash % 900000000) + 100000000;
  }

  // Single source of truth for BOTH the uid a visit role publishes with and
  // the id every viewer keys that role's tile on (me.info.<role>.id). They
  // must be the same expression: when the publisher used its logged-in
  // account id instead, viewers resolved the stream to "multiconnection" —
  // nameless tile, and moderatable by anyone (host cut by the host).
  agoraUidForRole(details: any, role: string) {
    switch (String(role || "").toLowerCase()) {
      case "guide":
        return this.toAgoraNumericId(
          details?.guide_user || details?.guide_details?.id || details?.guide_user_id
        );
      case "host":
        // No `|| details.user` fallback: Visit.host_user is nullable while
        // Visit.user (the manager) is not, so an unassigned host would publish
        // the admin's uid and Agora would drop whichever of the two joined
        // first. The namespaced fallback is derived from the visit, so every
        // client still computes the same id for the host tile.
        return this.toAgoraNumericId(
          details?.host_user ||
            details?.host_details?.id ||
            details?.host_user_id ||
            (details?.id ? `host:${details.id}` : "")
        );
      case "residence":
        return this.toAgoraNumericId(
          details?.structure || details?.structure_details?.id || details?.residence_user_id
        );
      case "admin":
        return this.toAgoraNumericId(
          details?.user || details?.manager_details?.id || details?.admin_user_id
        );
      default:
        return 0;
    }
  }

  private getVisitLinkByRole(visit: any, role: string) {
    if (!Array.isArray(visit?.visit_links)) {
      return "";
    }

    const linkRecord = visit.visit_links.find((entry: any) => {
      return String(entry?.role || "").toLowerCase() === role;
    });

    return linkRecord?.link || "";
  }

  private mapInvitees(invitees: any[] = []) {
    return invitees.map((invitee: any, index: number) => ({
      // Numeric like every other participant id: getUserRoleById/getUserNameById
      // compare these against Agora streamIDs, and the invitee publishes with it.
      // `user_id` is the key Django writes for participants registered through
      // the waiting-room endpoint.
      id: this.toAgoraNumericId(
        invitee?.id || invitee?.invitee_user_id || invitee?.user_id || `invitee-${index}`
      ),
      name: invitee?.full_name || invitee?.name || invitee?.user_name || "Invitee",
      user_name: invitee?.full_name || invitee?.name || invitee?.user_name || "Invitee",
      user_role: "Invitee",
      status: invitee?.status || "PENDING",
      link: invitee?.link || "",
    }));
  }

  private mapVisitRecord(visit: any, currentUser?: any) {
    const isoDate = this.combineDateTime(visit.date, visit.time);
    const themeName =
      // Theme display_name is a translatable JSON dict — raw it renders as
      // "[object Object]" in the visit title.
      this.resolveLocalizedValue(visit?.theme_details?.display_name) ||
      this.resolveLocalizedValue(visit?.theme_details?.name) ||
      "Visit";
    const structureName =
      visit?.structure_details?.display_name ||
      this.resolveLocalizedValue(visit?.structure_details?.name) ||
      "Residence";
    const guideName = this.formatUserName(visit?.guide_details);
    const hostName = this.formatUserName(visit?.host_details) || this.formatUserName(visit?.manager_details);
    const adminName = this.isAdminUser(currentUser) ? this.formatUserName(currentUser) : "Admin";
    const invitees = this.mapInvitees(Array.isArray(visit?.invitees) ? visit.invitees : []);
    const backendRole = String(currentUser?.groups?.[0] || "").toLowerCase();
    const explicitPublicRole = String(visit?.user_role || "").trim().toLowerCase();
    const roleFromLink = this.resolveRoleFromCurrentVisitLink(visit);
    // The visit's own FKs decide before group membership: groups[0] ordering is
    // arbitrary, and a staff account in several groups must still enter as the
    // guide/host THIS visit designates (its tile id is derived from that FK).
    const accountId = String(currentUser?.id || "");
    const roleFromVisit = !accountId
      ? ""
      : accountId === String(visit?.guide_user || "")
      ? "guide"
      : accountId === String(visit?.host_user || "")
      ? "host"
      : "";
    const userRole =
      explicitPublicRole ||
      roleFromVisit ||
      backendRole ||
      this.resolveUserRole(currentUser) ||
      roleFromLink ||
      "residence";
    const visitId = visit?.visit_id || visit?.id || "";
    const guideAgoraId = this.toAgoraNumericId(visit?.guide_user || visit?.guide_details?.id);
    const hostAgoraId = this.toAgoraNumericId(
      visit?.host_user || visit?.host_details?.id
    );
    const residenceAgoraId = this.toAgoraNumericId(visit?.structure || visit?.structure_details?.id);
    const adminAgoraId = this.isAdminUser(currentUser) ? this.toAgoraNumericId(currentUser?.id) : 0;
    const inviteePrimary = invitees[0] || { id: "", name: "", user_role: "Invitee", status: "PENDING", link: "" };
    const guideLink = this.getVisitLinkByRole(visit, "guide");
    const hostLink = this.getVisitLinkByRole(visit, "host");
    const residenceLink = this.getVisitLinkByRole(visit, "residence");
    const adminLink = this.getVisitLinkByRole(visit, "admin");

    const participants = [
      {
        id: guideAgoraId,
        name: guideName,
        user_name: guideName,
        user_role: "MultiConnection",
        status: "PENDING",
        link: guideLink,
      },
      {
        id: residenceAgoraId,
        name: structureName,
        user_name: structureName,
        user_role: "Residence",
        status: "PENDING",
        link: residenceLink,
      },
      {
        id: hostAgoraId,
        name: hostName,
        user_name: hostName,
        user_role: "Host",
        status: "PENDING",
        link: hostLink,
      },
      ...invitees,
    ];

    return {
      ...visit,
      id: visitId || visit?.id,
      visit: visitId || visit?.id,
      visit_id: visitId || visit?.id,
      visit_name: `${themeName}${structureName ? ` - ${structureName}` : ""}`,
      residence_name: structureName,
      host_name: hostName,
      guide_name: guideName,
      guide_picture: visit?.guide_details?.profile_image || "",
      visit_picture: visit?.theme_details?.photos?.[0]?.image_url || visit?.structure_details?.photo_url || "",
      residence_latitude: visit?.structure_details?.lat || null,
      residence_longitude: visit?.structure_details?.lng || null,
      guide_latitude: visit?.guide_details?.lat || null,
      guide_longitude: visit?.guide_details?.lng || null,
      residence_type:
        visit?.structure_details?.structure_type ||
        visit?.structure_details?.type_display ||
        visit?.structure_details?.type ||
        "",
      // Multi-connexion flag the moderation UI (Mute All) gates on. Django's
      // Visit exposes the boolean `is_multi_connection`; keep the legacy
      // spellings + the structure-type fallback for robustness.
      is_multiconnection:
        visit?.is_multi_connection === true ||
        visit?.is_multiconnection === true ||
        (visit?.structure_details?.structure_type ||
          visit?.structure_details?.type_display ||
          visit?.structure_details?.type) === "Multi-Connection Client",
      city: visit?.structure_details?.city || visit?.structure_details?.region || "",
      postalCode: visit?.structure_details?.zip_code || visit?.structure_details?.postal_code || "",
      details: this.resolveLocalizedValue(visit?.theme_details?.description) || "",
      time: visit?.date || this.formatDisplayDate(isoDate),
      visit_time: visit?.time || "",
      time_in_utc: isoDate,
      time_raw: isoDate,
      duration: visit?.theme_details?.duration_minutes || null,
      status: visit?.status,
      user_role: userRole,
      connected_user_id: currentUser?.id || "",
      guide_user_id: guideAgoraId,
      host_user_id: hostAgoraId,
      residence_user_id: residenceAgoraId,
      admin_user_id: adminAgoraId,
      guide_username: guideName,
      host_username: hostName,
      admin_user_name: this.formatUserName(visit?.manager_details),
      guide_link: guideLink,
      host_link: hostLink,
      residence_link: residenceLink,
      admin_link: adminLink,
      comments: this.resolveLocalizedValue(visit?.theme_details?.description) || "",
      // Must be the REAL "guide has started" boolean (Django default False),
      // NOT derived from booking status: a scheduled visit is confirmed/ready
      // long before the guide starts, and deriving it from status made
      // residences skip the waiting room + guide approval.
      visit_started: Boolean(visit?.visit_started),
      participants,
      // Override the raw backend list: me.info.invitees is compared against
      // Agora streamIDs (getUserRoleById, getUserNameById, filterMembersList),
      // so it must carry the same numeric ids as `participants`.
      invitees,
      invitee: inviteePrimary,
      admin_name: adminName,
      new_visit_level: visit?.category || "",
    };
  }

  private async fetchCurrentUser() {
    try {
      const res = await Axios.get(ApiUrls.currentUserUrl, this.getAuthHeaders());
      const userGroups = res.data.groups || [];
      if (res.data.is_superuser || ["Guide","Host", "Host Commercial", "Host Billing", "Super Host", "Admin"].some(group => userGroups.includes(group))) {
        return res.data;
      }
      throw new Error("Unauthorized user role");
    } catch (error) {
      return null;
    }
  }

  async getAuthorizedCurrentUser() {
    return this.fetchCurrentUser();
  }

  private isGuideUser(user: any) {
    return user?.groups.includes("Guide")
  }
  private isHostUser(user: any) {
    return user?.groups.some((group: string) => ["Host", "Host Commercial", "Host Billing", "Super Host"].includes(group));
  }

  private isAdminUser(user: any) {
    return user?.is_superuser || user?.groups.includes("Admin");
  }

  private isResidenceUser(user: any) {
    return user?.structure ? true : false;
  }

  private mapVisitCollection(rows: any[], currentUser?: any) {
    return rows.map((row) => this.mapVisitRecord(row, currentUser));
  }

  initialize() {
    this.subject = new Subject<EventRoomState>();
    this.state = {
      ...this.defaultState,
    };
    this.subject.next(this.state);
  }

  subscribe(updateState: any) {
    this.initialize();
    this.subject && this.subject.subscribe(updateState);
  }

  unsubscribe() {
    this.subject && this.subject.unsubscribe();
    this.subject = null;
  }

  commit(state: EventRoomState) {
    this.subject && this.subject.next(state);
  }

  updateState(rootState: EventRoomState) {
    this.state = {
      ...this.state,
      ...rootState,
    };
    this.commit(this.state);
  }

  addLocalStream(stream: AgoraStream) {
    this.updateLocalStream(stream);
    console.log("Add local stream ?????????: ", stream)
  }

  updateAutoPlayFailedStatus(flag: boolean) {
    this.state = {
      ...this.state,
      rtc: {
        ...this.state.rtc,
        ["isAutoPlayFailed"]: flag,
      },
    };
    this.commit(this.state);
  }

  updateAdminVideoPlayerStatus(flag: boolean) {
    this.state = {
      ...this.state,
      showAdminVideoPlayer: flag
    };
    this.commit(this.state);
  }

  addInjectedStream(stream: AgoraStream, key: string = "rtc") {
    this.updateLocalStream(stream, "injectedStream");
  }

  handleMainExit(role: any, redirect_url: any) {
    this.rtmClient.exit();
    sessionStorage.removeItem("visitStarted");
    window.location.href = redirect_url;
  }

  removeLocalStream(stream: any = null, key: string = "localStream") {
    try {
      const localStream =
        key === "localStream" ? this.state.rtc.localStream : "";
      if (
        localStream &&
        localStream.stream.videoTrack &&
        localStream.stream.videoTrack.isPlaying()
      ) {
        //localStream.stream.stop();
      }
      this.updateLocalStream(stream, key);
    } catch (e) { }
  }

  removeInjectedStream = () => {
    this.state.rtc.injectedStream
      ? this.state.rtc.injectedStream.stream?.videoTrack.stop()
      : null;
    this.updateLocalStream(null, "injectedStream");
  };

  updateLocalStream(stream: any = null, key: string = "localStream") {
    this.state = {
      ...this.state,
      rtc: {
        ...this.state.rtc,
        [key]: stream,
      },
    };
    this.commit(this.state);

    console.log("Updating local stream ??????: ", this.state)
  }
  setPinId = (value: number) => {
    this.state = {
      ...this.state,
      pinId: value,
    };
    this.commit(this.state);
  };

  handleRemotePin = (memberId: any) => {
    let Pindata = {
      type: ChatCmdType.pinid,
      msgData: { streamID: memberId },
    };
    this.rtmClient.sendChannelMessage(Pindata, this.state.me.channelName);
  };

  addRTCUser(uid: number, key: string = "rtc") {
    const prevState = this.state.rtc;
    this.state = {
      ...this.state,
      [key]: {
        ...prevState,
        users: prevState.users.add(uid),
      },
    };
    this.commit(this.state);
  }

  removePeerUser(uid: number, key: string = "rtc") {
    const prevState = this.state.rtc;
    this.state = {
      ...this.state,
      [key]: {
        ...prevState,
        users: prevState.users.delete(uid),
      },
    };
    this.commit(this.state);
  }

  addRemoteStream(stream: AgoraStream, key: string = "rtc") {
    const prevState = this.state.rtc;
    this.state = {
      ...this.state,
      [key]: {
        ...prevState,
        remoteStreams: prevState.remoteStreams.set(
          `${stream.streamID}`,
          stream
        ),
      },
    };

    this.commit(this.state);

    console.log("Adding remote stream ?????: ", stream)
  }

  removeRemoteStream(uid: number, key: string = "rtc") {
    const prevState = this.state.rtc;
    const remoteStream = prevState.remoteStreams.get(`${uid}`);

    this.state = {
      ...this.state,
      [key]: {
        ...prevState,
        remoteStreams: prevState.remoteStreams.delete(`${uid}`),
      },
    };
    if (
      typeof remoteStream?.stream.audioTrack != "undefined" &&
      remoteStream?.stream.audioTrack != null
    ) {
      remoteStream ? remoteStream.stream?.audioTrack?.stop() : null;
    }
    if (
      typeof remoteStream?.stream.videoTrack != "undefined" &&
      remoteStream?.stream.videoTrack != null
    ) {
      remoteStream ? remoteStream.stream?.videoTrack.stop() : null;
    }
    this.commit(this.state);
  }

  updateDevice(state: MediaDeviceState) {
    this.state = {
      ...this.state,
      mediaDevice: state,
    };
    this.commit(this.state);
  }

  setMeAttr(key: string, uid: number) {
    this.state = {
      ...this.state,
      me: {
        ...this.state.me,
        [key]: uid,
      },
    };
    this.commit(this.state);
  }

  setRTCToken(token: string) {
    this.state = {
      ...this.state,
      rtcToken: token,
    };
    this.commit(this.state);
  }

  updateWaitingList(waitinglist: any) {
    this.state = {
      ...this.state,
      waitingList: waitinglist,
    };
    this.commit(this.state);
  }

  setRTCJoined(joined: boolean) {
    this.state = {
      ...this.state,
      rtc: {
        ...this.state.rtc,
        joined: joined,
      },
    };
    this.commit(this.state);
  }

  updateChatMessage(msg: ChatMessage) {
    this.state = {
      ...this.state,
      messages: this.state.messages.push(msg),
      isMessageSent: this.state.isMessageSent ? true : false,
    };

    this.commit(this.state);
  }

  updatePrivateChatMessage(msg: ChatMessage) {
    this.state = {
      ...this.state,
      privateMessages: this.state.privateMessages.push(msg),
      isMessageSent: this.state.isMessageSent ? true : false,
    };

    this.commit(this.state);
  }

  updateGridView(flag: boolean) {
    this.state = {
      ...this.state,
      gridView: flag,
    };
    this.commit(this.state);
    if (flag) {
      eventRoomStore.setMeAttr("mainLiveStreamID", 0);
    }
  }

  updateNoiseCancellation(flag: boolean) {
    this.state = {
      ...this.state,
      is_noise_cancellation_on: flag,
    };
    this.commit(this.state);
  }

  updateSilentResidenceFeature(flag: boolean) {
    this.state = {
      ...this.state,
      is_residence_silent: flag,
    };
    this.commit(this.state);
  }

  hasVisitStarted(flag: boolean) {
    this.state = {
      ...this.state,
      hasStarted: flag,
    };
    this.commit(this.state);
  }

  async sendMessage(data: ChannelParams) {
    try {
      if (!this.state.rtm.joined) return;
      await this.rtmClient.sendChannelMessage(data, this.state.me.channelName);

      if (data.type === ChatCmdType.chat) {
        this.updateChatMessage(data.msgData);
      }
    } catch (e) { }
  }

  async sendPrivateMessage(uid: string, data: MessageBody) {
    try {
      if (!this.state.rtm.joined) return;
      await this.rtmClient.sendPeerMessage(
        uid.toString(),
        data,
        this.state.me.channelName
      );

      if (data.type === ChatCmdType.chat) {
        this.updatePrivateChatMessage(data.msgData);
      }
    } catch (e) {
      console.error("Error sending peer message:", e);
    }
  }

  async exitAll() {
    try {
      this.rtcClient.exit();
      this.rtmClient.exit();
      let noStreamElem = document.querySelector(
        ".large-class .main-stream-section .main-stream.no-video"
      ) as HTMLElement;
      if (noStreamElem != null) {
        noStreamElem.style.backgroundColor = "transparent";
      }
    } catch (err) {
      console.warn(err);
    }
    finally {
    }
  }

  async highlightStreamSpeaking() {
    try {
      await this.rtcClient.volumeHighlights();
      console.log("Volume indicator initialized successfully");
    } catch (err) {
      console.warn(err);
    }
  }

  private compositeMe(params: Partial<Me>): Me {
    const newMe: Me = { ...this.state.me };
    for (const prop in params) {
      if (newMe.hasOwnProperty(prop) && params.hasOwnProperty(prop)) {
        set(newMe, prop, get(params, prop, ""));
      }
    }
    return newMe;
  }

  async updateLocalMe(params: Partial<Me>) {
    const { ...meParams } = params;
    const newMe = this.compositeMe(meParams);

    this.state = {
      ...this.state,
      me: {
        ...this.state.me,
        ...newMe,
      },
    };
    this.commit(this.state);
  }

  async mute(uid: string, type: string) {
    console.log("Mute ???????")
    const me = this.state.me;
    if (`${me.uid}` === `${uid}`) {
      const streamType = "localStream";
      const localStream: any = this.state.rtc[streamType];
      if (localStream) {
        localStream[type] = false;
        this.updateLocalStream(localStream, streamType);
      }
    } else {
      const remoteStream: any = this.state.rtc.remoteStreams.get(`${uid}`);
      if (remoteStream) {
        remoteStream[type] = false;
        this.addRemoteStream(remoteStream);
      } else {
      }
    }
  }

  async updateRemoteDetails(
    type: string,
    streamID: any,
    track: any,
    details: any
  ) {
    const name = details?.name;
    const prevState = this.state.rtc;
    let remoteStream = prevState.remoteStreams.get(`${streamID}`) as any;
    let key = "remoteStream";

    if (typeof remoteStream == "undefined") {
      // Self-heal: a subscribed track arrived while the stream entry was absent
      // from the store. Happens when the publisher drops and rejoins (e.g. guide
      // network loss): a user-left — possibly a LATE one from the dead session —
      // removes the entry right around the republish, and this used to silently
      // return: track subscribed at SDK level but never attached → permanent
      // silence with zero errors anywhere ("host hears the guide, residence
      // doesn't", 3× on 2026-07-07). Recreate the entry and attach.
      console.warn("[rtc-event] updateRemoteDetails: stream entry missing — self-healing", { streamID, type });
      Sentry.captureMessage("[rtc-event] remote track without stream entry — self-healed", "warning");
      this.addRemoteStreamDetails(
        streamID,
        { audioTrack: null, videoTrack: null },
        details,
        this.getUserRoleById(streamID)
      );
      remoteStream = this.state.rtc.remoteStreams.get(`${streamID}`) as any;
      if (typeof remoteStream == "undefined") {
        console.error("[rtc-event] self-heal FAILED — stream entry still missing", { streamID, type });
        return;
      }
    }

    remoteStream["stream"][type] = track;
    if (type == "videoTrack") {
      remoteStream["video"] = true;
      remoteStream["videoTurnedOn"] = true;
    } else {
      remoteStream["audio"] = true;
    }
    remoteStream["name"] = name;

    if (key == "injectedStream") {
      this.state = {
        ...this.state,
        ["rtc"]: {
          ...prevState,
          injectedStream: remoteStream,
        },
      };
    } else {
      this.state = {
        ...this.state,
        ["rtc"]: {
          ...prevState,
          remoteStreams: prevState.remoteStreams.set(
            `${streamID}`,
            remoteStream
          ),
        },
      };
    }

    this.commit(this.state);
  }

  async unmute(uid: string, type: string) {
    const me = this.state.me;
    if (`${me.uid}` === `${uid}`) {
      const streamType = "localStream";
      const localStream: any = this.state.rtc[streamType];
      localStream[type] = true;
      this.updateLocalStream(localStream, streamType);
    } else {
      const remoteStream: any = this.state.rtc.remoteStreams.get(`${uid}`);
      if (remoteStream) {
        // Defensive fix: for video unmute via RTM, only set video=true if the videoTrack
        // actually exists. If the track is null/undefined (RTM arrived before RTC user-published),
        // skip setting the flag - updateRemoteDetails() will set it when the track arrives.
        if ((type === "video" || type === "videoTurnedOn") && !remoteStream?.stream?.videoTrack) {
          console.warn("[event-store] unmute: skipping", type, "for uid", uid, "- videoTrack not yet available");
          return;
        }
        remoteStream[type] = true;
        this.addRemoteStream(remoteStream);
      } else {
      }
    }
  }

  async loginAndJoin(token: string) {
    const { uid, channelName } = this.state.me;

    try {
      await this.rtmClient.login(APP_ID, uid.toString(), token);
      await this.rtmClient.join(channelName);
      this.state = {
        ...this.state,
        rtm: {
          ...this.state.rtm,
          joined: true,
        },
        viewerCount: 0,
      };
      this.commit(this.state);

      // Presence needs time after subscribe before getOnlineUsers can be queried.
      // Calling too early triggers the SDK bug in PresenceImpl._requestRPCWhoNow
      // which corrupts the client's internal state and breaks message delivery.
      setTimeout(() => {
        this.getViewerCount([channelName])
          .then((viewerCount) => {
            if (typeof viewerCount === "number") {
              this.state = { ...this.state, viewerCount };
              this.commit(this.state);
            }
          })
          .catch(() => {});
      }, 1500);
      return;
    } catch (err) {
      if (this.rtmClient._logged) {
        await this.rtmClient.logout();
      }
      throw err;
    }
  }

  async getViewerCount(channel: string[]) {
    try {
      const viewerCount = await this.rtmClient.getChannelMemberCount(channel);
      return viewerCount;
    } catch (e) { }
  }

  updateViewerCount = (count: number) => {
    this.state = {
      ...this.state,
      viewerCount: count,
    };
    this.commit(this.state);
  };

  async getMembersList(channel: string) {
    try {
      if (!channel) return;
      const res: string[] = await this.rtmClient.getChannelMembersList(channel);

      const membersList = Set(res);
      const updatedMembersList = this.filterMembersList(res);
      this.state = {
        ...this.state,
        usersAsAudience: updatedMembersList,
      };
      this.commit(this.state);
    } catch (e) { }
  }

  filterMembersList = (membersList: any) => {
    const invitees = this.state.me.info?.invitees || [];
    const knownMemberIds = [
      this.state.me.info?.guide?.id,
      this.state.me.info?.host?.id,
      this.state.me.info?.residence?.id,
      this.state.me.info?.admin?.id,
      this.state.me.uid,
    ].filter((value) => value !== undefined && value !== null && value !== "");

    const normalizedMembers = (Array.isArray(membersList) ? membersList : []).filter((member: any) => {
      const memberId = typeof member === "string" ? member : member?.userId ?? member?.id ?? member?.user_id;
      if (!memberId) {
        return false;
      }

      const memberIdStr = String(memberId);
      const isKnownMember = knownMemberIds.some((knownId) => String(knownId) === memberIdStr);
      if (isKnownMember) {
        return true;
      }

      return invitees.some((invitee: any) => {
        const candidateIds = [
          invitee?.id,
          invitee?.user_id,
          invitee?.userId,
          invitee?.invitee_user_id,
        ];
        return candidateIds.some((candidate) => String(candidate) === memberIdStr);
      });
    });

    return normalizedMembers;
  };

  handleStreamControlClick = (type: string, active: boolean) => {
    const me = this.state.me;
    if (!me.uid) return console.warn("please confirm joined rtc");
    const targetUser = me;
    if (!targetUser) return;
    const targetUid = String(targetUser.uid);
    if (type == "video" || "videoTurnedOn" || type == "audio") {
      if (active) {
        this.mute(targetUid, type);
      } else {
        this.unmute(targetUid, type);
      }
    }
  };

  handleRemoteAudio = (memberId: any, status: any) => {
    if (!this.canModerateTarget(memberId)) {
      console.warn("[moderation] refused: protected target", { memberId, status, myRole: this.state.me.info.role });
      return;
    }
    let audioData = {
      type: ChatCmdType.muteAudio,
      msgData: {audio: status === "unmute" ? true: false, streamId: memberId},
    };
    if (status === "unmute") {
      audioData.type = ChatCmdType.unmuteAudio;
    }
    
    eventRoomStore.sendMessage(audioData);
  };

  handleResidenceSilent = (memberId: any) => {
    let SilentData = {
      type: ChatCmdType.isSilent,
      msgData: {},
    };

    this.rtmClient.sendPeerMessage(
      memberId.toString(),
      SilentData,
      this.state.me.channelName
    );
  };

  handleRemoteVideo = (memberId: any, status: any) => {
    if (!this.canModerateTarget(memberId)) {
      console.warn("[moderation] refused: protected target", { memberId, status, myRole: this.state.me.info.role });
      return;
    }
    let cmd = ChatCmdType.muteVideo;
    let videoStatus = false;
    if(status === "mute") {
      eventRoomStore.mute(memberId.toString(), "video");
      eventRoomStore.mute(memberId.toString(), "videoTurnedOn");
    } else {
      videoStatus = true;
      cmd = ChatCmdType.unmuteVideo;
      eventRoomStore.unmute(memberId.toString(), "video");
      eventRoomStore.unmute(memberId.toString(), "videoTurnedOn");
    }

    let videoData = {
      cmd,
      data: {
        streamId: memberId,
        video: videoStatus
      },
    };
    // if (status == "unmute") {
    //   videoData.cmd = ChatCmdType.unmuteVideo;
    // }
    eventRoomStore.sendMessage(
      {
        type: videoData.cmd,
        msgData: videoData.data,
      }
    );
  };

  // Roles that can never be muted / camera-cut / removed by another
  // participant, whatever the caller's role. Mirrors the Mute-All exclusions
  // but is enforced per target, on the send side (UI + store) and on the
  // receive side (agora-rtm-client), so a mis-resolved sender cannot bypass it.
  static PROTECTED_ROLES = ["guide", "host", "admin"];

  isProtectedRole = (role: any): boolean =>
    EventRoomStore.PROTECTED_ROLES.includes(String(role || "").toLowerCase());

  // True when the stream must not be moderated: myself, any uid the visit
  // record designates as guide/host/admin, or a stream resolved to one of those
  // roles. The id check works even before the remote stream entry exists.
  isProtectedTarget = (streamID: any): boolean => {
    const id = Number(streamID);
    if (!Number.isFinite(id)) return true;
    if (Number(this.state.me.uid) === id) return true;
    const info: any = this.state.me.info || {};
    if ([info.guide?.id, info.host?.id, info.admin?.id].some((x) => Number(x) === id)) {
      return true;
    }
    const remote: any = this.state.rtc.remoteStreams.get(`${streamID}`);
    return this.isProtectedRole(remote?.userRole);
  };

  canModerateTarget = (streamID: any): boolean =>
    this.isProtectedRole(this.state.me.info.role) && !this.isProtectedTarget(streamID);

  /**
   * Retourne les rôles exclus du "Mute All" selon le rôle de l'appelant
   * - Host/Admin: ne peuvent pas muter le guide
   * - Guide: ne peut pas muter host/admin
   */
  getExcludedRolesForMuteAll = (): string[] => {
    const myRole = this.state.me.info.role;

    if (['host', 'admin'].includes(myRole)) {
      return ['guide', 'host', 'admin'];
    }
    if (myRole === 'guide') {
      return ['host', 'admin', 'guide'];
    }
    return [];
  };

  /**
   * Vérifie si l'utilisateur peut initier un "Mute All"
   */
  canInitiateMuteAll = (): boolean => {
    return ['host', 'admin', 'guide'].includes(this.state.me.info.role);
  };

  handleRemoteAudioForAll = (status: any) => {
    if (!this.canInitiateMuteAll()) return;

    const excludedRoles = this.getExcludedRolesForMuteAll();

    // Parcourir tous les remote streams et muter ceux qui ne sont pas exclus
    this.state.rtc.remoteStreams.forEach((stream: any, uid: string) => {
      const streamRole = stream.userRole || '';

      // Ne pas muter les rôles exclus, ni une cible protégée par son id
      // (rôle non résolu sur le stream mais désigné par la visite).
      if (excludedRoles.includes(streamRole) || this.isProtectedTarget(uid)) return;

      // Utiliser la méthode existante pour muter individuellement
      this.handleRemoteAudio(parseInt(uid), status);
    });
  };

  handleRemoteVideoForAll = (status: any) => {
    if (!this.canInitiateMuteAll()) return;

    const excludedRoles = this.getExcludedRolesForMuteAll();

    // Parcourir tous les remote streams et muter ceux qui ne sont pas exclus
    this.state.rtc.remoteStreams.forEach((stream: any, uid: string) => {
      const streamRole = stream.userRole || '';

      // Ne pas muter les rôles exclus, ni une cible protégée par son id
      // (rôle non résolu sur le stream mais désigné par la visite).
      if (excludedRoles.includes(streamRole) || this.isProtectedTarget(uid)) return;

      // Utiliser la méthode existante pour muter individuellement
      this.handleRemoteVideo(parseInt(uid), status);
    });
  };

  setNetworkQuality = (value: number) => {
    // Agora reports every ~2s; committing an unchanged value re-renders every
    // store subscriber for nothing.
    if (this.state.networkQuality === value) return;
    this.state = {
      ...this.state,
      networkQuality: value,
    };
    this.commit(this.state);
  };

  getAgoraTokens = async (
    channelName: any,
    userId: any,
    type: string = "rtc",
    isScreenShareToken?: boolean
  ) => {
    try {
      const hasAuth = Object.keys(this.getAuthHeaders()).length > 0;
      const endpoint = hasAuth ? ApiUrls.AgoraUrl : ApiUrls.publicAgoraUrl;
      const config = hasAuth ? this.getAuthHeaders() : {};
      const res = await Axios.post(endpoint, {
        action: 'generate_token',
        channelName,
        userId: `${userId}`,
        type,
        isScreenShareToken
      }, config);
      const { rtcToken, rtmToken } = res?.data?.data;
      return type == "rtc" ? rtcToken : rtmToken;
    } catch (e: any) {
      console.log(e)
      throw new Error(e);
    }
  };
  // const res = await Axios.post(ApiUrls.AgoraUrl, {
  //   channelName,
  //   userId: `${userId}`,
  //   type
  // });
  // const { rtcToken, rtmToken } = res?.data?.data;
  // return type == "rtc" ? rtcToken : rtmToken;
  // };

  getUserNameById = (userId: any) => {
    let userName = "";
    console.log("Invitee search", this.state.me.info?.invitees);
    console.log("Invitee search id", userId);
    if (userId == this.state.me.info?.guide?.id) {
      userName = this.state.me.info?.guide.name;
    } else if (userId == this.state.me.info?.host?.id) {
      userName = "Equipe Travel Me";
    } else if (userId == this.state.me.info?.residence?.id) {
      userName = this.state.me.info?.residence.name;
    } else if (userId == this.state.me.info?.admin?.id) {
      userName = this.state.me.info?.admin.name;
    } else {
      for (let UserId of this.state.me.info?.invitees) {
        console.log("invitees Id", UserId);
        if (UserId.id == `${userId}`) {
          console.log("inside if invitees Id", UserId);
          userName = UserId.name;
        }
      }
    }
    return userName;
  };

  getUserRoleById = (userType: any) => {
    console.log("getUserRoleById", userType);
    let user_role = "";
    if (userType == this.state.me.info?.guide.id) {
      user_role = this.state.me.info?.guide.userRole;
    } else if (userType == this.state.me.info?.host.id) {
      user_role = this.state.me.info?.host.userRole;
    } else if (userType == this.state.me.info?.residence.id) {
      user_role = this.state.me.info?.residence.userRole;
    } else if (userType == this.state.me.info?.admin?.id) {
      user_role = this.state.me.info?.admin.userRole;
    } else if (this.state.me.info?.invitees) {
      for (let UserId of this.state.me.info?.invitees) {
        console.log("invitees Id", userType);
        if (UserId.id == userType) {
          user_role = "invitee";
        }
      }
    }
    return user_role || "multiconnection";
  };

  addRemoteStreamDetails = (
    streamID: any,
    stream: any,
    details: any,
    userRole: any
  ) => {
    const _stream = new AgoraStream(
      stream,
      streamID,
      false,
      false,
      false,
      false,
      details && details?.name ? details?.name : "",
      "",
      userRole
    );
    if (+streamID === INJECT_STREAM_ID) {
      eventRoomStore.setMeAttr("mainLiveStreamID", 0);
      sessionStorage.setItem("injected", "priority");
      this.updateGridView(false);
      this.addInjectedStream(_stream);
      console.log("adding stream id", streamID);
    } else {
      this.addRTCUser(streamID);
      if (streamID === SCREEN_SHARE_STREAM_ID) {
        this.updateGridView(false);
        eventRoomStore.setMeAttr("mainLiveStreamID", 0);
        sessionStorage.removeItem("injected");
      }
      this.addRemoteStream(_stream);

      console.log("stream id is ", streamID);
    }
  };

  updateCurrentMicrophone = (microphoneId: any) => {
    this.state = {
      ...this.state,
      currentMicrophone: microphoneId,
    };
    this.commit(this.state);
  };

  updateCurrentCamera = (cameraId: any) => {
    this.state = {
      ...this.state,
      currentCamera: cameraId,
    };
    this.commit(this.state);
  };

  updateCurrentSpeaker = (speakerId: any) => {
    this.state = {
      ...this.state,
      currentSpeaker: speakerId,
    };
    this.commit(this.state);
  };

  handleDeviceChange = () => {
    AgoraRTC.onMicrophoneChanged = async (changedDevice: any) => {
      if (changedDevice.state != "ACTIVE") {
        const oldMicrophones = await AgoraRTC.getMicrophones();
        if (oldMicrophones[0]) {
          if (
            typeof this.state.rtc.localStream?.stream.audioTrack !=
            "undefined" &&
            this.state.rtc.localStream?.stream.audioTrack != null
          ) {
            this.state.rtc.localStream?.stream.audioTrack.setDevice(
              oldMicrophones[0].deviceId
            );
            window.sessionStorage.setItem(
              "microphoneId",
              oldMicrophones[0].deviceId
            );
            this.updateCurrentMicrophone(oldMicrophones[0].deviceId);
          }
        }
      }
    };

    AgoraRTC.onCameraChanged = async (changedDevice: any) => {
      if (changedDevice.state != "ACTIVE") {
        const oldCameras = await AgoraRTC.getCameras();
        if (oldCameras[0]) {
          if (
            typeof this.state.rtc.localStream?.stream.videoTrack !=
            "undefined" &&
            this.state.rtc.localStream?.stream.videoTrack != null
          ) {
            this.state.rtc.localStream?.stream.videoTrack.setDevice(
              oldCameras[0].deviceId
            );
            window.sessionStorage.setItem("cameraId", oldCameras[0].deviceId);
            this.updateCurrentCamera(oldCameras[0].deviceId);
          }
        }
      }
    };

    AgoraRTC.onPlaybackDeviceChanged = async (changedDevice: any) => {
      if (changedDevice.state != "ACTIVE") {
        const playBackDevices = await AgoraRTC.getPlaybackDevices();
        if (playBackDevices[0]) {
          if (
            typeof this.state.rtc.localStream?.stream.videoTrack !=
            "undefined" &&
            this.state.rtc.localStream?.stream.videoTrack != null
          ) {
            // Output-device selection is Chromium-only; Firefox/Safari throw
            // NOT_SUPPORTED and this handler was UNcaught (TM-FRONTEND-17).
            // Feature-detect; still remember the device for browsers that can.
            const audioTrack = this.state.rtc.localStream?.stream.audioTrack;
            if (audioTrack && typeof audioTrack.setPlaybackDevice === "function") {
              try {
                audioTrack.setPlaybackDevice(playBackDevices[0].deviceId);
              } catch (e) {
                console.warn("[audio-pipeline] setPlaybackDevice failed on device change", e);
              }
            }
            window.sessionStorage.setItem(
              "speakerId",
              playBackDevices[0].deviceId
            );
            this.updateCurrentSpeaker(playBackDevices[0].deviceId);
          }
        }
      }
    };
  };

  ExitFullScreenMode = () => {
    try {
      if (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement
      ) {
        let rightSideElement = document.querySelector(
          "section.sidebar-container._right"
        ) as any;
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
      return "success";
    } catch (err) {
      return "error";
    }
  };

  convertTimeFromUTC = (date: any) => {
    return new Date(date);
  };

  isVisitStarterRole = (role?: string) => {
    return ["guide", "host", "admin"].includes(String(role || "").toLowerCase());
  };

  canStartVisit = (timeinUtc: any) => {
    const visit_date: any = this.convertTimeFromUTC(timeinUtc);
    const today: any = new Date();
    const differenceMinutes = (visit_date.getTime() - today.getTime()) / 60000;
    if (differenceMinutes < 20) {
      return true;
    } else {
      return false;
    }
  };

  fetchVisitDetails = async (id: any) => {
    const response: { status: number; visit: any } = { status: 0, visit: null };
    try {
      if (Object.keys(this.getAuthHeaders()).length > 0) {
      const [currentUser, res] = await Promise.all([
        this.fetchCurrentUser(),
        Axios.get(ApiUrls.visitDetailUrl(id), this.getAuthHeaders()),
      ]);
      response.status = res?.status;
        response.visit = this.mapVisitRecord(res?.data, currentUser);
      } else {
        const res = await Axios.get(ApiUrls.unAuthUserVisitDetailUrl(id));
        response.status = res?.status;
        response.visit = this.mapVisitRecord(res?.data, null);
      }
    } catch (e: any) {
      response.status = e?.response?.status;
    }
    return { ...response };
  };

  fetchadminvisitDetails = async (id: any, limit: any, page: any) => {
    const response = { status: 0, visit: "", total: 0 };
    try {
      const [currentUser, res] = await Promise.all([
        this.fetchCurrentUser(),
        Axios.get(ApiUrls.visitDetailUrl(id), this.getAuthHeaders()),
      ]);
      response.status = res?.status;
      response.visit = this.mapVisitRecord(res?.data, currentUser);
      response.total = 1;
    } catch (e: any) {
      response.status = e?.response?.status;
    }
    return response;
  };

  fetchUniqueId = async () => {
    try {
      const res = await Axios.get(ApiUrls.UniqueIdGenerator);
      if (res.status === 200) {
        return res.data?.data;
      }
    } catch (e: any) { }
  };

  fetchUserDetails = async (
    visit_id: any = this._state.me.channelName,
    user_id: any
  ) => {
    try {
      const res = await Axios.get(
        ApiUrls.UserDetails(visit_id, user_id),
        this.getAuthHeaders()
      );
      console.log("UserDetails", res);
      if (res.status === 200) {
        return res.data.data.user_name;
      } else {
        return "";
      }
    } catch (e: any) { }
  };

  SubmitParticipantdetails = async (
    visit_id: any,
    user_name: any,
    user_id: any,
    user_role: any
  ) => {
    try {
      this.state.me.info.name = user_name;
      const res = await Axios.post(
        ApiUrls.Particiapntdetails,
        {
          visit_id: visit_id,
          user_name: user_name,
          user_id: String(user_id),
          link: ApiUrls.Particiapntdetails,
          user_role: user_role,
        },
        this.getAuthHeaders()
      );
      console.log("SubmitParticipantdetails", res);
      if (res.status === 200) {
        return res.data?.data;
      }
    } catch (e: any) { }
  };

  fetchWaitingList = async (visit_id: any = this._state.me.channelName) => {
    try {
      const res = await Axios.get(ApiUrls.Waitinglist(visit_id), this.getAuthHeaders());
      if (res.status === 200) {
        this.updateWaitingList(res.data?.data || []);
        return res.data?.data || [];
      }
      return [];
    } catch (e: any) {
      return [];
    }
  };

  logout = () => {
    Cookies.remove("token");
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    sessionStorage.clear();
    this.history.push("/");
  };

  handleDeviceError = () => {
    toast.error(
      this.state.me.info.role == "guide"
        ? "Your audio or video device is not detected."
        : "Votre périphérique audio ou vidéo n'est pas détecté."
    );
  };

  async getAgoraVideoProfile() {
    if (this.state.me.info.role == "guide") {
      try {
        const hasAuth = Object.keys(this.getAuthHeaders()).length > 0;
        const endpoint = hasAuth ? ApiUrls.agoraVideoResolution : ApiUrls.publicAgoraVideoResolution;
        const config = hasAuth ? this.getAuthHeaders() : {};
        const res = await Axios.post(
          endpoint,
          { action: "video_resolution" },
          config
        );
        if (res?.data?.data?.web) {
          return res?.data?.data?.web;
        } else {
          return "1080p_5";
        }
      } catch (e) {
        return "1080p_5";
      }
    } else {
      return "480p_1";
    }
  }

  getAgoraLowStreamProfile = () => {
    if (this.state.me.info.role == "guide") {
      return [640, 480, 15, 500];
    } else {
      return [640, 360, 15, 400];
    }
  };

  fetchGuideVisits = async (page: any, limit: any) => {
    const response = { status: 0, visits: { total_visits: 0, entries: [] as any[] } };
    try {
      const currentUser = await this.fetchCurrentUser();
      const guideId = this.isGuideUser(currentUser) ? currentUser?.id : null;
      const hostId = this.isHostUser(currentUser) ? currentUser?.id : null;
      const url = guideId
        ? `${ApiUrls.visitListUrl(page, limit)}&guide=${guideId}`
        : hostId
        ? `${ApiUrls.visitListUrl(page, limit)}&host=${hostId}`
        : ApiUrls.visitListUrl(page, limit);
      const res = await Axios.get(url, this.getAuthHeaders());
      const rows = res?.data?.results || [];
      response.status = res?.status;
      response.visits = {
        total_visits: res?.data?.count || rows.length,
        entries: this.mapVisitCollection(rows, currentUser).filter((visit: any) => {
          if (!guideId) {
            return true;
          }
          return String(visit.guide_user) === String(guideId) || String(visit?.guide_details?.id) === String(guideId);
        }),
      };
    } catch (e: any) {
      response.status = e?.response?.status;
      if (!response.status) {
        if (e?.message?.includes("401")) {
          response.status = 401;
        }
      }
    }
    return response;
  };

  fetchVisitsHistory = async (from?: string) => {
    const response: { status: number; history: any } = { status: 0, history: null };
    try {
      const res = await Axios.get(ApiUrls.visitsHistoryUrl(from || "2026-01-01"), this.getAuthHeaders());
      const rows = res?.data?.results || [];
      response.status = res?.status;
      response.history = {
        total: res?.data?.count || rows.length,
        entries: this.mapVisitCollection(rows).filter((visit: any) =>
          ["completed", "no_show"].includes(visit.status)
        ),
      };
    } catch (e: any) {
      response.status = e?.response?.status;
      if (!response.status) {
        if (e?.message?.includes("401")) {
          response.status = 401;
        }
      }
    }
    return response;
  };

  fetchUserVisits = async (userId: string, page: any, limit: any) => {
    const response = { status: 0, visits: { total_visits: 0, entries: [] as any[] } };
    try {
      const currentUser = await this.fetchCurrentUser();
      const url = this.isHostUser(currentUser)
        ? `${ApiUrls.visitListUrl(page, limit)}&host=${userId}`
        : ApiUrls.visitListUrl(page, limit);
      const res = await Axios.get(url, this.getAuthHeaders());
      const rows = res?.data?.results || [];
      response.status = res?.status;
      response.visits = {
        total_visits: res?.data?.count || rows.length,
        entries: this.mapVisitCollection(rows, currentUser).filter((visit: any) => {
          return true;
        }),
      };
    } catch (e: any) {
      response.status = e?.response?.status;
      if (!response.status) {
        if (e?.message?.includes("401")) {
          response.status = 401;
        }
      }
    }
    return response;
  };
}

export const eventRoomStore = new EventRoomStore();

// TODO: Please remove it before release in production
// 备注：请在正式发布时删除操作的window属性
//@ts-ignore
window.eventRoomStore = eventRoomStore;